/**
 * Coda sincronizzazione: invio ordini creati offline quando torna la connettività.
 * Enqueue ordini, processa su evento online, retry con exponential backoff.
 */

import { Network } from '@capacitor/network';
import { BASE_URL } from '../../consts/baseUrl';
import { store } from '../../stores';
import { authFetch } from '../../utils/authUtils';
import {
  initSqliteStore,
  syncQueueEnqueue,
  syncQueueGetPending,
  syncQueueUpdateEntry,
  syncQueueIncrementAttempts,
  syncQueueGetPendingCount,
  syncQueueSetIdempotencyKey,
  localOrderUpdateState,
} from './sqlite-store.service';
import { idempotencyService } from '../../orm/idempotency';
import { retryService } from '../sync/retry.service';
import type { CreateOrderDto } from '../api/interfaces/create-order.interface';
import type { SyncQueueEntry } from './sqlite-store-types';

type QueueOperation = 'create_order' | 'update_order' | 'delete_order';

const SYNC_FAILURE_EVENT = 'sync-failure';

async function ensureInit(): Promise<void> {
  await initSqliteStore();
}

function getAccessToken(): string | null {
  return store.getState().auth.accessToken;
}

/**
 * Mappa payload da LocalOrder a CreateOrderDto per POST /api/orders.
 */
function localOrderToCreateOrderDto(payload: {
  order: { delivery_date: string; delivery_time?: string | null; customer_code: string; customer_name: string; notes?: string | null; draft_code?: string | null };
  items: Array<{ item_code: string; quantity: number; default_unit_price: number; default_price: number; unit_selling_price: number; selling_price: number; notes?: string | null }>;
  salesByItem?: Record<string, Array<{ name: string; percentValue?: number; value?: number }>>;
}): CreateOrderDto {
  const o = payload.order;
  const items = payload.items;
  const salesByItem = payload.salesByItem ?? {};

  return {
    deliveryDate: o.delivery_date,
    deliveryTime: o.delivery_time ?? undefined,
    customerCommercialCode: o.customer_code,
    customerName: o.customer_name,
    orderNotes: o.notes ?? undefined,
    draftCode: o.draft_code ?? undefined,
    items: items.map((item) => {
      const itemSales = salesByItem[item.item_code] ?? [];
      const discount1 = itemSales.find((s) => s.name === 'c1')?.percentValue ?? 0;
      const discount2 = itemSales.find((s) => s.name === 'c2')?.percentValue ?? 0;
      const discount3 = itemSales.find((s) => s.name === 'i1')?.percentValue ?? 0;
      const discount4 = itemSales.find((s) => s.name === 'i2')?.percentValue ?? 0;
      return {
        itemCode: item.item_code,
        quantity: item.quantity,
        defaultUnitPrice: item.default_unit_price,
        defaultPrice: item.default_price,
        sellingUnitPrice: item.unit_selling_price,
        sellingPrice: item.selling_price,
        discount1: discount1 || undefined,
        discount2: discount2 || undefined,
        discount3: discount3 || undefined,
        discount4: discount4 || undefined,
        productNotes: item.notes ?? undefined,
      };
    }),
  };
}

export const syncQueueService = {
  _isProcessing: false,
  _networkListener: null as { remove: () => Promise<void> } | null,

  /**
   * Inizializza il listener di rete per sync automatico.
   */
  async initialize(): Promise<void> {
    await ensureInit();
    try {
      this._networkListener = await Network.addListener('networkStatusChange', async (status) => {
        if (status.connected) {
          await this.processQueue();
        }
      });
      const status = await Network.getStatus();
      if (status.connected) {
        await this.processQueue();
      }
    } catch {
      if (typeof window !== 'undefined') {
        const onOnline = () => void this.processQueue();
        window.addEventListener('online', onOnline);
        this._networkListener = {
          remove: () => {
            window.removeEventListener('online', onOnline);
            return Promise.resolve();
          },
        } as { remove: () => Promise<void> };
        if (navigator.onLine) {
          await this.processQueue();
        }
      }
    }
  },

  /**
   * Aggiunge un'operazione alla coda.
   */
  async enqueue(
    operation: QueueOperation,
    entityType: string,
    entityId: string,
    payload: unknown
  ): Promise<void> {
    await ensureInit();
    await syncQueueEnqueue(operation, entityType, entityId, JSON.stringify(payload));
    try {
      const status = await Network.getStatus();
      if (status.connected) {
        void this.processQueue();
      }
    } catch {
      if (typeof navigator !== 'undefined' && navigator.onLine) {
        void this.processQueue();
      }
    }
  },

  /**
   * Processa la coda di sincronizzazione.
   */
  async processQueue(): Promise<void> {
    if (this._isProcessing) return;
    this._isProcessing = true;

    try {
      await ensureInit();
      const entries = await syncQueueGetPending(10);

      for (const entry of entries) {
        await syncQueueIncrementAttempts(entry.id);
        const attemptsAfterIncrement = entry.attempts + 1;

        try {
          const execResult = await retryService.withRetry(
            () => this._executeOperation(entry),
            { maxAttempts: 3, initialDelayMs: 2000 }
          );

          await syncQueueUpdateEntry(
            entry.id,
            'completed',
            null,
            new Date().toISOString()
          );
          if (entry.operation !== 'delete_order') {
            await localOrderUpdateState(
              entry.entity_id,
              'synced',
              null,
              execResult?.serverId
            );
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          const isPermanentFailure = attemptsAfterIncrement >= entry.max_attempts;

          if (isPermanentFailure) {
            await syncQueueUpdateEntry(
              entry.id,
              'failed',
              errorMessage,
              new Date().toISOString()
            );
            if (entry.operation !== 'delete_order') {
              await localOrderUpdateState(entry.entity_id, 'error', errorMessage);
            }
            this._notifyPermanentFailure(entry, errorMessage);
          } else {
            await syncQueueUpdateEntry(entry.id, 'pending', errorMessage);
          }
        }
      }
    } finally {
      this._isProcessing = false;
    }
  },

  async _executeOperation(
    entry: SyncQueueEntry
  ): Promise<{ serverId?: string } | void> {
    if (entry.operation === 'create_order') {
      const payload = JSON.parse(entry.payload_json) as {
        order: { delivery_date: string; delivery_time?: string | null; customer_code: string; customer_name: string; notes?: string | null; draft_code?: string | null };
        items: Array<{ item_code: string; quantity: number; default_unit_price: number; default_price: number; unit_selling_price: number; selling_price: number; notes?: string | null }>;
        salesByItem?: Record<string, Array<{ name: string; percentValue?: number; value?: number }>>;
      };
      const result = await idempotencyService.executeIdempotent(
        {
          operationType: 'create_order',
          localEntityId: entry.entity_id,
          payload,
        },
        async (idempotencyKey) => {
          await syncQueueSetIdempotencyKey(entry.id, idempotencyKey);
          const body = localOrderToCreateOrderDto(payload);
          const token = getAccessToken();
          if (!token) throw new Error('Token non disponibile');
          const response = await authFetch(`${BASE_URL}/api/orders`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              'Idempotency-Key': idempotencyKey,
            },
            body: JSON.stringify(body),
          });
          const data = (await response.json().catch(() => ({}))) as {
            id?: string;
            message?: string;
            error?: string;
          };
          if (!response.ok) {
            const message =
              (typeof data?.message === 'string' ? data.message : null) ??
              (typeof data?.error === 'string' ? data.error : null) ??
              response.statusText;
            throw new Error(`${response.status} ${message}`);
          }
          return { data, serverId: data.id };
        }
      );
      return { serverId: result.serverId };
    } else if (entry.operation === 'update_order') {
      const payload = JSON.parse(entry.payload_json) as { serverId: string; [k: string]: unknown };
      const serverId = payload.serverId;
      if (!serverId) throw new Error('Server ID required for update');
      const result = await idempotencyService.executeIdempotent(
        {
          operationType: 'update_order',
          localEntityId: entry.entity_id,
          payload,
        },
        async (idempotencyKey) => {
          await syncQueueSetIdempotencyKey(entry.id, idempotencyKey);
          const token = getAccessToken();
          if (!token) throw new Error('Token non disponibile');
          const response = await authFetch(`${BASE_URL}/api/orders/${serverId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              'Idempotency-Key': idempotencyKey,
            },
            body: JSON.stringify(payload),
          });
          if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
          const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
          return { data, serverId };
        }
      );
      return { serverId };
    } else if (entry.operation === 'delete_order') {
      const payload = JSON.parse(entry.payload_json) as { serverId: string };
      const token = getAccessToken();
      if (!token) throw new Error('Token non disponibile');
      const response = await authFetch(`${BASE_URL}/api/orders/${payload.serverId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
    }
  },

  _notifyPermanentFailure(entry: SyncQueueEntry, errorMessage: string): void {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(SYNC_FAILURE_EVENT, {
          detail: {
            operation: entry.operation,
            entityId: entry.entity_id,
            error: errorMessage,
          },
        })
      );
    }
  },

  /**
   * Conta le operazioni in coda.
   */
  async getPendingCount(): Promise<number> {
    await ensureInit();
    return syncQueueGetPendingCount();
  },

  destroy(): void {
    this._networkListener?.remove();
    this._networkListener = null;
  },
};
